import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  getSyncState, subscribeSync, refreshSync, runSync, dismissFailed,
} from './training/offlineSync';
import '../styles/SyncStatus.css';

// Keeps the offline queue moving, and says so when it is not empty.
//
// Mounted once for the whole app, so sets queued in the logger are sent even
// after the athlete has left it: on load, when the phone comes back online,
// and every 30 seconds while anything is waiting.
//
// The chip itself is hidden on the two logging screens. The logger's own header
// shows the count there, and a chip would sit where "Log set" is tapped.

const RETRY_MS = 30_000;
const LOGGING = /^\/programs\/[^/]+\/sessions\/[^/]+\/(train|log)\/?$/;

export function showsSyncChip(pathname) {
  return !LOGGING.test(pathname || '');
}

const SyncStatus = () => {
  const { pathname } = useLocation();
  const { username, token, isAuthenticated } = useAuth();
  const [sync, setSync] = useState(getSyncState);
  const [open, setOpen] = useState(false);

  useEffect(() => subscribeSync(setSync), []);

  // On sign-in (and on load): read what is waiting and try to send it.
  useEffect(() => {
    if (!isAuthenticated || !username) {
      refreshSync(null);
      return undefined;
    }
    refreshSync(username);
    runSync({ user: username, token });

    const onOnline = () => runSync({ user: username, token });
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [isAuthenticated, username, token]);

  // While anything is waiting, keep trying. Some networks come back without
  // an `online` event (a captive portal, a flaky gym wifi that never fully
  // dropped).
  const waiting = sync.user === username ? sync.pending.length : 0;
  useEffect(() => {
    if (!waiting || !username) return undefined;
    const id = setInterval(() => runSync({ user: username, token }), RETRY_MS);
    return () => clearInterval(id);
  }, [waiting, username, token]);

  if (!showsSyncChip(pathname) || sync.user !== username) return null;
  const failed = sync.failed;
  if (!waiting && !failed.length) return null;

  const plural = (n) => `${n} ${n === 1 ? 'set' : 'sets'}`;
  let message;
  if (sync.needsSignIn && waiting) message = `Sign in to sync ${plural(waiting)}`;
  else if (waiting) message = `${plural(waiting)} waiting to sync`;
  else message = `${plural(failed.length)} could not sync`;

  return (
    <div className={`sync-status${failed.length ? ' sync-status--failed' : ''}`} role="status">
      <div className="sync-status-row">
        <span>{message}</span>
        {failed.length > 0 && (
          <button type="button" className="btn-ghost" onClick={() => setOpen((o) => !o)}>
            {open ? 'Hide' : 'Why?'}
          </button>
        )}
      </div>
      {open && failed.length > 0 && (
        <ul className="sync-status-list">
          {failed.map((item) => (
            <li key={item.id}>
              <span>{item.label || `Set ${item.body?.setNumber}`}: {item.reason}</span>
              <button type="button" className="btn-ghost" onClick={() => dismissFailed(username, item.id)}>
                Dismiss
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SyncStatus;
